<template>
  <div
    id="keywords_table"
    class="white_box"
  >
    <div class="heading_wrapper">
      <h3 class="heading">
        Keywords
      </h3>
      <form class="search_keyword_form">
        <input
          ref="keywordInput"
          v-model="searchTerm"
          autocomplete="off"
          class="search_keyword_input"
          placeholder="Search Your Key Products &amp; Services"
          type="search"
        >
      </form>

      <div
        v-if="typeFilter || keywordFilter || competitorFilter"
        class="clear_filter"
        @click="clearAllFilters()"
      >
        Clear
        filters <span class="delete" />
      </div>

      <div
        class="filter_wrapper"
        @mouseleave="filterMenu = false"
        @mouseover="filterMenu = true"
      >
        <button class="select_btn">
          <span>Filter: </span>
          <i class="icon arrow_down_icon" />
          <span>{{ filterNiceName }}</span>
        </button>
        <ul
          v-show="filterMenu"
          class="filter_dropdown"
          @mouseleave="filterMenu = false"
          @mouseover="filterMenu = true"
        >
          <li>
            <button @click="setFilter({key:'typeFilter', value: 'keyword_ideas'}); filterMenu = false;">
              Keyword Ideas
            </button>
          </li>
          <li>
            <button @click="setFilter({key:'typeFilter', value: 'ranked_keyword'}); filterMenu = false;">
              Your Ranked Keywords
            </button>
          </li>
          <li>
            <button @click="setFilter({key:'typeFilter', value: 'competitor_keywords'}); filterMenu = false;">
              Competitor Keywords
            </button>
          </li>
          <li>
            <button @click="setFilter({key:'typeFilter', value: 'blog_ideas'}); filterMenu = false;">
              Blog ideas
            </button>
          </li>
          <hr>
          <li class="all_item">
            <button @click="setFilter({key:'typeFilter', value: null}); filterMenu = false;">
              All Keywords
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="table_wrapper">
      <table class="keywords_table head">
        <thead>
          <tr>
            <td>
              <div
                :class="[
                  'sort_title',
                  sortBy === 'keyword' ? sortDirection : ''
                ]"
                @click="setSort('keyword')"
              >
                <span>Keyword</span>
                <i class="icon arrow_down" />
              </div>
            </td>
            <td>
              <span>Source</span>
            </td>
            <td>
              <div
                :class="[
                  'sort_title',
                  'center',
                  sortBy === 'search_volume' ? sortDirection : '',
                ]"
                @click="setSort('search_volume')"
              >
                <span>Search Volume</span>
                <svg-vue
                  icon="icons/info-circle"
                  class="title_info tooltipster"
                  :title="'Search volume is a measure of the average number of searches that '+
                    'are performed through Google per month'"
                />
                <i class="icon arrow_down" />
              </div>
            </td>
            <td>
              <div
                :class="[
                  'sort_title',
                  'center',
                  sortBy === 'difficulty' ? sortDirection : '',
                ]"
                @click="setSort('difficulty')"
              >
                <span>Difficulty</span>
                <svg-vue
                  icon="icons/info-circle"
                  class="title_info tooltipster"
                  :title="'Keyword difficulty is a metric indicating how difficult it is to rank in the '+
                    'top 10 search results for a keyword. Try to pick keywords with a lower difficulty value where '+
                    'possible, as it should be easier to rank in Google.'"
                />
                <i class="icon arrow_down" />
              </div>
            </td>
            <td v-if="canEditKeywords">
              &nbsp;
            </td>
          </tr>
        </thead>
      </table>
      <div
        ref="scrollableTable"
        class="table_body_wrap"
      >
        <table class="keywords_table">
          <tbody>
            <tr
              v-for="keyword in formattedSuggestions"
              :key="keyword.id"
            >
              <td class="keyword">
                {{ keyword.keyword }}
              </td>
              <td>
                <KeywordOpportunitySources :sources="keyword.pivot.sources" />
              </td>
              <td class="volume">
                {{ (keyword.search_volume > 0 ? keyword.search_volume.toLocaleString() : '< 10') }}
              </td>
              <td class="difficulty">
                <difficulty-tag
                  :difficulty="
                    keyword.difficulty !== 999
                      ? keyword.difficulty
                      : keyword.calculated_difficulty
                  "
                />
              </td>
              <td v-if="canEditKeywords">
                <button
                  v-if="keyword.chosen"
                  class="row_button chosen"
                  @click="removeKeyword(keyword)"
                >
                  <i class="fas fa-check" />
                </button>
                <button
                  v-else
                  class="row_button add"
                  @click="addKeyword(keyword)"
                />
              </td>
            </tr>
            <tr>
              <td
                v-if="suggestions.length === 0"
                class="text-center"
                colspan="5"
              >
                <i
                  v-if="loadingSuggestions"
                  class="fas fa-spinner fa-spin"
                />
                {{ loadingSuggestions ? 'Loading suggestions' : 'No results found' }}
                {{ typeFilter || keywordFilter || competitorFilter ? 'with your current filters or search' : '' }}
              </td>
            </tr>
            <tr ref="loadMoreRow">
              <td
                v-if="canLoadMoreSuggestions"
                class="text-center"
                colspan="5"
              >
                <i class="fas fa-spinner fa-spin" />
                Loading more suggestions
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="keyword_table_footer">
      <span
        class="negative_keywords"
        @click="showNegativeKeywords = true"
      >
        + Add negative keywords
        <small v-if="ignoreFilters.length > 0">
          ({{ ignoreFilters.length }} filter(s) applied)
        </small>
      </span>
      <button
        :class="'btn btn_md secondary_btn '+(highlightButton ? 'highlight': '')"
        @click="viewChosenKeywords"
      >
        {{ totalKeywords }}/{{ keywordLimit }} Chosen Keywords
      </button>
      <button
        v-if="showSaveKeywordsButton"
        class="btn btn_md primary_btn save"
        @click="viewChosenKeywords"
      >
        Save Keywords
      </button>
    </div>
  </div>

  <review-keywords-modal
    v-if="showChosenKeywordsModal"
    @updated-keywords="$emit('updatedKeywords')"
  />
  <negative-keywords-modal v-if="showNegativeKeywords" />

  <KeywordKitChangesModal
    v-if="showKitChangesModal"
    @close="showKitChangesModal = false"
  />
</template>

<script>
import _ from 'lodash'
import Swal from 'sweetalert2';
import {mapActions, mapGetters} from 'vuex';
import ReviewKeywordsModal from '../modals/review/ReviewKeywordsModal.vue';
import NegativeKeywordsModal from './../modals/NegativeKeywordsModal.vue';
import DifficultyTag from "../../../../../js/components/global/assets/DifficultyTag";
import KeywordOpportunitySources from "./KeywordOpportunitySources.vue";
import KeywordKitChangesModal from "../modals/KeywordKitChangesModal.vue";

export default {
  components: {
    KeywordKitChangesModal,
    KeywordOpportunitySources,
    DifficultyTag,
    ReviewKeywordsModal,
    NegativeKeywordsModal
  },
  emits: ['updatedKeywords'],
  data() {
    return {
      filterMenu: false,
      filter: 'All Keywords',
      visibleRows: 50,
      showChosenKeywords: false,
      showNegativeKeywords: false,
      highlightButton: false,
      showHelpVideo: false,
      showKitChangesModal: false,
    }
  },
  computed: {
    ...mapGetters('keywordIdeas', [
      'keywordLimit',
      'chosenKeywords',
      'savedChosenKeywords',
      'newChosenKeywords',
      'removeChosenKeywords',
      'suggestions',
      'typeFilter',
      'keywordFilter',
      'competitorFilter',
      'searchFilter',
      'loadingSuggestions',
      'showChosenKeywordsModal',
      'onboarding',
      'canLoadMoreSuggestions',
      'sortBy',
      'sortDirection',
      'ignoreFilters',
    ]),
    ...mapGetters('user', [
      'canEditKeywords'
    ]),
    searchTerm: {
      get() {
        return this.searchFilter;
      },
      set(value) {
        this.setFilter({key: 'searchFilter', value});
      }
    },
    filterNiceName() {
      switch (this.typeFilter) {
        case null:
          return 'All Keywords';
        case 'keyword_ideas':
          return 'Keyword Ideas';
        case 'ranked_keyword':
          return 'Your Ranked Keywords';
        case 'competitor_keywords':
          return 'Competitor Keywords';
        case 'blog_ideas':
          return 'Blog Ideas';
      }
      return '';
    },
    totalKeywords() {
      return this.chosenKeywords.length + this.newChosenKeywords.length;
    },
    showSaveKeywordsButton() {
      return (
        this.newChosenKeywords.length > 0 ||
        this.removeChosenKeywords.length > 0
      ) && !this.onboarding
    },
    formattedSuggestions() {
      return this.suggestions.map((keyword) => {

        return {
          ...keyword,
          search_volume: keyword.search_volume || 0,
          chosen: this.isChosen(keyword),
          new: this.isNew(keyword),
        };
      });
    }
  },
  watch: {
    showSaveKeywordsButton: {
      handler: function (newValue) {
        this.showKitChangesModal = newValue;
      },
      immediate: true,
    }
  },
  mounted() {
    // Prevent refresh if users press enter in the search box
    this.$refs.keywordInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });

    this.$refs.scrollableTable.onscroll = () => {
      this.loadMoreSuggestionsAtEndOfScroll(
        this.$refs.scrollableTable,
        this.$refs.loadMoreRow
      );
    };
  },
  methods: {
    ...mapActions('keywordIdeas', [
      'addNewChosenKeyword',
      'removeNewChosenKeyword',
      'removeChosenKeyword',
      'ignoreRemoveChosenKeyword',
      'setFilter',
      'clearAllFilters',
      'openChosenKeywordsModal',
      'fetchSuggestions',
      'setSort'
    ]),
    loadMoreSuggestionsAtEndOfScroll(container, element) {

      if (!this.canLoadMoreSuggestions) {
        return;
      }

      // Get container properties
      let containerTop = container.scrollTop;
      let containerBottom = containerTop + container.clientHeight;

      // Get element properties
      let elementTop = element.offsetTop;
      let elementBottom = elementTop + element.clientHeight;

      // Check if in view
      let inPartialView = (
        (elementTop < containerTop && elementBottom > containerTop) ||
        (elementBottom > containerBottom && elementTop < containerBottom)
      );

      if (inPartialView) {
        this.fetchSuggestionsThrottled();
      }
    },
    fetchSuggestionsThrottled: _.throttle(function () {
        this.fetchSuggestions();
      },
      500,
      { 'trailing': false }
    ),
    addKeyword(keyword) {
      const savedChosenKeywordIndex = _.findIndex(this.savedChosenKeywords, {
        keyword: keyword.keyword
      });

      let index = 0;
      if (savedChosenKeywordIndex !== -1) {
        index = _.findIndex(this.removeChosenKeywords, {keyword: keyword.keyword});
        this.ignoreRemoveChosenKeyword(index);
      } else {
        if ((this.chosenKeywords.length + this.newChosenKeywords.length) < this.keywordLimit) {
          this.addNewChosenKeyword(keyword);
        } else {
          Swal.fire({
            title: `Limit Reached`,
            icon: 'warning',
            html: `You've reached your keyword limit of ${this.keywordLimit}. Please remove some keywords before adding more, or <a href="/keywords/order" target="_blank">increase</a> your limit.`,
            confirmButtonText: 'Ok',
            backdrop: true,
          })
        }
      }

      window.mixpanel('Keyword Ideas: Add Keyword To Basket', { keyword: keyword.keyword });

      this.highlightButton = true;
      setTimeout(() => {
        this.highlightButton = false;
      }, 1000);
    },
    removeKeyword(keyword) {
      if (keyword.new) {
        let index = _.findIndex(this.newChosenKeywords, {keyword: keyword.keyword});
        this.removeNewChosenKeyword(index);
      } else if (keyword.chosen) {
        let index = _.findIndex(this.chosenKeywords, {keyword: keyword.keyword});
        this.removeChosenKeyword(index);
      }
      this.highlightButton = true;
      setTimeout(() => {
        this.highlightButton = false;
      }, 1000);
    },
    isChosen(keyword) {
      const existsInChosenKeywords = _.findIndex(this.chosenKeywords, {
        keyword: keyword.keyword
      }) !== -1;
      const existsInNewChosenKeywords = _.findIndex(this.newChosenKeywords, {
        keyword: keyword.keyword
      }) !== -1

      return (
        existsInChosenKeywords
        || existsInNewChosenKeywords
      );
    },
    isNew(keyword) {
      return _.findIndex(this.newChosenKeywords, {
        keyword: keyword.keyword
      }) !== -1;
    },
    viewChosenKeywords() {
      window.mixpanel('Keyword Ideas: View Chosen Keywords');
      this.openChosenKeywordsModal();
    },
  },
}
</script>

<style scoped>
#keywords_table {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* Header */
.heading_wrapper {
  display: flex;
  align-items: center;
  height: 10%;
}

.heading {
  font-weight: 700;
  line-height: 1.42;
  letter-spacing: -0.042em;
  color: #040707;
  font-size: 1.5rem;
  margin: 0;
}

.search_keyword_form {
  width: 40%;
  margin-left: 1rem;
  margin-right: auto;
}

.search_keyword_input {
  width: 100%;
  background-repeat: no-repeat;
  background-position: 16px center;
  -webkit-appearance: textfield;
  -moz-appearance: textfield;
  appearance: textfield;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='17' viewBox='0 0 16 17' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.33337 13.8333C10.6471 13.8333 13.3334 11.147 13.3334 7.83334C13.3334 4.51963 10.6471 1.83334 7.33337 1.83334C4.01967 1.83334 1.33337 4.51963 1.33337 7.83334C1.33337 11.147 4.01967 13.8333 7.33337 13.8333Z' stroke='%23040707' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M14.6667 15.1667L12 12.5' stroke='%23040707' stroke-width='1.33333' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  padding: 1rem;
  padding-left: 2.5rem;
  border-radius: 12px;
  background-color: #F7F5F2;
  height: 42px;
  font-size: 14px;
  letter-spacing: -0.5px;
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
  line-height: normal;
}

.filter_wrapper {
  border-radius: 0.75rem;
  font-size: 14px;
  width: 220px;
}

.select_btn {
  display: flex;
  align-items: center;
}

.select_wrapper .select_btn i {
  -webkit-transform: scale(1);
  -ms-transform: scale(1);
  transform: scale(1);
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
}

.arrow_down_icon {
  margin: 0 5px;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.9631 5.65439L8.32102 10.2965L3.67891 5.65439' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
}

.arrow_down {
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 2.5V13.5' stroke='%23040707' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M3.5 9L8 13.5L12.5 9' stroke='%23040707' stroke-width='0.875' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
}

.clear_filter {
  margin-right: 2rem;
}

.delete {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  display: block;
  margin-left: 4px;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.375 2.625L2.625 9.375' stroke='%234E4E54' stroke-width='0.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M9.375 9.375L2.625 2.625' stroke='%234E4E54' stroke-width='0.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: center;
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
}

.filter_dropdown {
  list-style: none;
  color: var(--secondary-font-color);
  z-index: 10;
  position: absolute;
  width: 215px;
  padding: 1rem;
  background-color: var(--white);
  border: 1px solid #C7C4C1;
  border-radius: 0.75rem;
  transition: .3s;
  -webkit-transition: .3s;
  -o-transition: .3s;
}

.filter_dropdown li button {
  color: var(--secondary-font-color);
  display: flex;
  display: -webkit-box;
  display: -ms-flexbox;
  align-items: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  text-align: left;
  padding: .25rem .5rem;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: -0.1px;
  border-radius: .5rem;
  font-weight: 400;
}

.filter_dropdown li button:hover {
  background-color: #FFEEEA;
  font-weight: 500;
}

/* End Header */
/* Table */
.table_wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: 80%;
}

.keywords_table {
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
}

.keywords_table.head {
  height: 15%;
}

.keywords_table thead td {
  padding: 1rem 1rem 1rem;
}

.keywords_table thead td span {
  font-weight: 500;
  font-size: 16px;
  line-height: 26px;
  letter-spacing: -0.25px;
  margin-right: 0.25rem;
}

.sort_title {
  cursor: pointer;
  display: flex;
  align-items: center;
}
.title_info {
  height: 16px;
  width: 16px;
}

.sort_title.center {
  justify-content: center;
}

.sort_title.desc i {
  transform: rotate(180deg);
}

.table_body_wrap {
  max-height: 85%;
  overflow-y: scroll;
}

.keywords_table tr td:first-of-type {
  width: 30%;
}

.keywords_table tr td:nth-of-type(2) {
  width: 30%;
}

.keywords_table tr td:nth-of-type(3) {
  width: 20%;
  text-align: center;
}

.keywords_table tr td:nth-of-type(4) {
  width: 15%;
  text-align: center;
}

.keywords_table tr td:nth-of-type(5) {
  width: 5%;
  padding: 0;
  text-align: center;
}

.keywords_table tbody tr td {
  padding: 1rem;
}

.keyword, .volume {
  font-size: 0.875rem;
  line-height: 1.72;
  letter-spacing: -0.00715em;
  color: var(--secondary-font-color);
}

.sources {
  display: flex;
  flex-wrap: wrap;
}

.competitor_source {
  background-color: var(--secondary-label-color);
  color: var(--secondary-color);
}

.ranked_source {
  background-color: var(--tertiary-label-color);
  color: var(--tertiary-color);
}

.ideas_source {
  background-color: var(--primary-label-color);
  color: var(--primary-color);
}

.difficulty span, .sources div {
  width: fit-content;
  padding: 0.25rem 0.5rem;
  font-size: 10px;
  line-height: 1.3;
  letter-spacing: -0.1px;
  border-radius: 6px;
  margin: 2px;
}

.row_button {
  width: 30px;
  height: 30px;
  cursor: pointer;
  border-radius: 6px;
}

.row_button.add {
  border: 2px solid #37B2A8;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.1875 7H11.8125' stroke='%2337B2A8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M7 2.1875V11.8125' stroke='%2337B2A8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: center;
}

.row_button.chosen {
  background-color: #6cc58c;
  border-color: #6cc58c;
  color: #ffffff;
}

/* End Table */
/* Footer */
.keyword_table_footer {
  height: 10%;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  background-color: #fff !important;
  margin-top: 10px;
}

.keyword_table_footer .btn {
  margin: 0;
  height: 42px;
  padding-top: 0;
  padding-bottom: 0;
  display: inline-flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
}

.highlight {
  box-shadow: 0 0 10px #8362F9;
}

.negative_keywords {
  margin-right: auto;
  padding: 13px 1rem;
  font-size: 0.75rem;
  line-height: 1.334;
  letter-spacing: 0.00834em;
  color: #37B2A8;
  cursor: pointer;
}

.negative_keywords small {
  color: var(--negative-color);
}

.btn.save {
  margin-left: .5rem;
}

/* End Footer */

</style>
